import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Area,
  AreaChart,
  Bar,
  BarChart,
  ReferenceLine
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import './Chart.css';

const Chart = ({ data, hasMonthlyContribution = true }) => {
  const [chartType, setChartType] = useState('line');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { theme } = useTheme();
  
  // Efeito para monitorar o tamanho da tela e ajustar o gráfico conforme necessário
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determina se estamos em um dispositivo móvel
  const isMobile = windowWidth <= 767;
  
  // Otimiza os dados para o gráfico em dispositivos móveis 
  // reduzindo o número de pontos para melhor performance
  const optimizedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Se estiver em um dispositivo móvel e tiver muitos pontos, reduza a quantidade
    if (isMobile && data.length > 24) {
      const samplingRate = Math.ceil(data.length / 24);
      const sampledData = [];
      
      for (let i = 0; i < data.length; i += samplingRate) {
        sampledData.push(data[i]);
      }
      
      // Sempre inclui o último ponto para mostrar o resultado final
      if (sampledData[sampledData.length - 1] !== data[data.length - 1]) {
        sampledData.push(data[data.length - 1]);
      }
      
      return sampledData;
    }
    
    return data;
  }, [data, isMobile]);
  
  // Cores do tema para o gráfico
  const getChartColors = () => {
    return {
      totalValue: theme === 'dark' ? '#60a5fa' : '#3b82f6',
      investedAmount: theme === 'dark' ? '#34d399' : '#10b981',
      interestAmount: theme === 'dark' ? '#f59e0b' : '#f59e0b',
      cartesianGrid: theme === 'dark' ? '#334155' : '#e2e8f0',
      tooltipBg: theme === 'dark' ? '#1e293b' : '#fff',
      tooltipText: theme === 'dark' ? '#f1f5f9' : '#334155',
      axisText: theme === 'dark' ? '#94a3b8' : '#64748b',
    };
  };
  
  const chartColors = getChartColors();
  
  // Se não houver resultados, mostre um placeholder
  if (!data || data.length === 0) {
    return (
      <div className="chart-container placeholder">
        <h3>Visualização Gráfica</h3>
        <p>Preencha o formulário e clique em calcular para ver os resultados.</p>
      </div>
    );
  }
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };
  
  // Função para formatar o eixo X de acordo com o dispositivo
  const formatXAxis = (month) => {
    // Em dispositivos móveis, mostrar apenas alguns pontos do eixo
    if (isMobile) {
      const lastMonth = optimizedData[optimizedData.length - 1].month;
      
      if (month === 0 || month === lastMonth || month % 12 === 0) {
        if (month === 0) return '0';
        if (month < 12) return `${month}m`;
        return `${Math.floor(month / 12)}a`;
      }
      return '';
    }
    
    // Em desktops, mostrar mais detalhes
    if (month === 0) return '0';
    if (month < 12) return `${month}m`;
    if (month % 12 === 0) return `${month / 12}a`;
    return '';
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText }}>
          <p className="tooltip-title">{`Mês ${label}`}</p>
          <p className="tooltip-total">
            <span className="tooltip-value-label">Total: </span>
            <span className="tooltip-value">{formatCurrency(payload[0].value)}</span>
          </p>
          
          {hasMonthlyContribution && payload.length > 2 && (
            <>
              <p className="tooltip-invested">
                <span className="tooltip-value-label">Investido: </span>
                <span className="tooltip-value">{formatCurrency(payload[1].value)}</span>
              </p>
              <p className="tooltip-interest">
                <span className="tooltip-value-label">Rendimentos: </span>
                <span className="tooltip-value">{formatCurrency(payload[2].value)}</span>
              </p>
            </>
          )}
        </div>
      );
    }
    
    return null;
  };
  
  const renderChart = () => {
    switch (chartType) {
      case 'area':
        return (
          <AreaChart
            data={optimizedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.cartesianGrid} />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatXAxis} 
              tick={{ fill: chartColors.axisText }} 
            />
            <YAxis 
              tickFormatter={formatCurrency} 
              tick={{ fill: chartColors.axisText }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="totalAmount"
              stackId="1"
              stroke={chartColors.totalValue}
              fill={chartColors.totalValue}
              name="Total Acumulado"
            />
            {hasMonthlyContribution && (
              <>
                <Area
                  type="monotone"
                  dataKey="investedAmount"
                  stackId="2"
                  stroke={chartColors.investedAmount}
                  fill={chartColors.investedAmount}
                  name="Total Investido"
                />
                <Area
                  type="monotone"
                  dataKey="interestAmount"
                  stackId="3"
                  stroke={chartColors.interestAmount}
                  fill={chartColors.interestAmount}
                  name="Juros Acumulados"
                />
              </>
            )}
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart
            data={optimizedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.cartesianGrid} />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatXAxis} 
              tick={{ fill: chartColors.axisText }} 
            />
            <YAxis 
              tickFormatter={formatCurrency} 
              tick={{ fill: chartColors.axisText }} 
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="totalAmount" fill={chartColors.totalValue} name="Total Acumulado" />
            {hasMonthlyContribution && (
              <>
                <Bar dataKey="investedAmount" fill={chartColors.investedAmount} name="Total Investido" />
                <Bar dataKey="interestAmount" fill={chartColors.interestAmount} name="Juros Acumulados" />
              </>
            )}
          </BarChart>
        );
      case 'line':
      default:
        return (
          <LineChart
            data={optimizedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.cartesianGrid} />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatXAxis} 
              tick={{ fill: chartColors.axisText }} 
            />
            <YAxis 
              tickFormatter={formatCurrency} 
              tick={{ fill: chartColors.axisText }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="totalAmount"
              stroke={chartColors.totalValue}
              activeDot={{ r: 8 }}
              name="Total Acumulado"
              strokeWidth={2}
            />
            {hasMonthlyContribution && (
              <>
                <Line
                  type="monotone"
                  dataKey="investedAmount"
                  stroke={chartColors.investedAmount}
                  activeDot={{ r: 6 }}
                  name="Total Investido"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="interestAmount"
                  stroke={chartColors.interestAmount}
                  activeDot={{ r: 6 }}
                  name="Juros Acumulados"
                  strokeWidth={2}
                />
              </>
            )}
          </LineChart>
        );
    }
  };
  
  return (
    <div className="chart-container">
      <h3>Crescimento do Investimento</h3>
      <div className="chart-types">
        <button
          className={`chart-type-button ${chartType === 'line' ? 'active' : ''}`}
          onClick={() => setChartType('line')}
        >
          <span className="chart-icon">📈</span>
          <span className="chart-label">Linha</span>
        </button>
        <button
          className={`chart-type-button ${chartType === 'area' ? 'active' : ''}`}
          onClick={() => setChartType('area')}
        >
          <span className="chart-icon">📊</span>
          <span className="chart-label">Área</span>
        </button>
        <button
          className={`chart-type-button ${chartType === 'bar' ? 'active' : ''}`}
          onClick={() => setChartType('bar')}
        >
          <span className="chart-icon">📊</span>
          <span className="chart-label">Barras</span>
        </button>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
      <div className="chart-details">
        <p className="chart-footnote">
          <span className="info-icon">ℹ️</span>
          Dica: O gráfico mostra o crescimento do seu investimento ao longo do tempo.
        </p>
      </div>
    </div>
  );
};

export default Chart;